/**
 * Type definitions for skill visualization and impact analysis
 * Based on backend models from apps.school.models and apps.core.models
 */

export enum ImpactType {
  DAMAGE = 'Damage',
  HEAL = 'Heal',
  SHIELD = 'Shield',
  BUFF = 'Buff',
  DEBUFF = 'Debuff',
  STUN = 'Stun',
  SLEEP = 'Sleep',
  CONFUSION = 'Confusion',
  PARALYSIS = 'Paralysis',
  FREEZE = 'Freeze',
  BURN = 'Burn',
  POISON = 'Poison',
  SLOW = 'Slow',
  HASTE = 'Haste',
  BLIND = 'Blind',
  REFLECT = 'Reflect',
  ABSORB = 'Absorb',
  DODGE = 'Dodge',
  RESIST = 'Resist',
  IMMUNITY = 'Immunity',
  REGENERATION = 'Regeneration'
}

export enum ImpactViolationType {
  PHYSICAL = 'Physical',
  MENTAL = 'Mental',
  ENERGY = 'Energy',
  HEAT = 'Heat',
  COLD = 'Cold',
  LIGHT = 'Light',
  DARKNESS = 'Darkness',
  NONE = 'None'
}

export enum CharacterStats {
  PHYSICAL_STRENGTH = 'Physical Strength',
  MENTAL_STRENGTH = 'Mental Strength',
  FLOW_RESONANCE = 'Flow Resonance',
  CONCENTRATION = 'Concentration',
  FLOW_MANIPULATION = 'Flow Manipulation',
  FLOW_CONNECTION = 'Flow Connection',
  KNOWLEDGE = 'Knowledge',
  SPEED = 'Speed',
  LUCK = 'Luck',
  CHARISMA = 'Charisma'
}

export interface StatRequirement {
  stat: CharacterStats
  value: number
}

export interface Scaling {
  stat: CharacterStats
  value: number
}

export interface Formula {
  base: number
  requires?: StatRequirement[]
  scaling?: Scaling[]
  max_efficiency?: number
  min_efficiency?: number
}

export interface Impact {
  kind: ImpactType
  type: ImpactViolationType
  formula: Formula
}

export interface Cost {
  kind: 'Health' | 'Energy' | 'Action Points'
  value: number
}

export interface SkillForVisualization {
  id: number
  name: string
  description: string
  grade: number
  type: string
  icon?: string | null
  /** School primary keys are UUIDs; the skill payload carries the bare id. */
  schoolId?: string | null
  impact: Impact[]
  cost?: Cost[]
  min_range?: number
  max_range?: number
}

/**
 * Chart-specific types
 */
export interface DamageCalculationResult {
  min: number
  avg: number
  max: number
}

export interface ImpactChartData {
  impactIndex: number
  impact: Impact
  label: string // e.g., "Fireball (Heat Damage)"
  color: { r: number; g: number; b: number }
  data: {
    statValues: number[]
    minValues: number[]
    avgValues: number[]
    maxValues: number[]
  }
}

export interface SkillChartConfig {
  minStat: number
  maxStat: number
  statStep: number
  diceMin: number
  diceMax: number
}

/**
 * Gold-forward GM theme palette (restrained, no saturated primaries)
 */
export const CHART_COLORS = [
  { name: 'Gold', rgb: [250, 218, 149] as [number, number, number] },
  { name: 'Amber', rgb: [255, 215, 0] as [number, number, number] },
  { name: 'Sand', rgb: [201, 168, 106] as [number, number, number] },
  { name: 'Success', rgb: [143, 182, 60] as [number, number, number] },
  { name: 'Info', rgb: [127, 168, 201] as [number, number, number] },
  { name: 'Danger', rgb: [201, 105, 79] as [number, number, number] },
  { name: 'Bronze', rgb: [176, 141, 87] as [number, number, number] },
  { name: 'Ash', rgb: [154, 144, 120] as [number, number, number] }
]

/**
 * Violation type color mapping, mapped onto the GM gold-forward theme
 */
export const VIOLATION_TYPE_COLORS: Record<ImpactViolationType, { r: number; g: number; b: number }> = {
  [ImpactViolationType.PHYSICAL]: { r: 154, g: 144, b: 120 }, // gm-text-muted
  [ImpactViolationType.MENTAL]: { r: 201, g: 168, b: 106 }, // sand
  [ImpactViolationType.ENERGY]: { r: 127, g: 168, b: 201 }, // gm-info
  [ImpactViolationType.HEAT]: { r: 201, g: 105, b: 79 }, // warm ember (danger-adjacent)
  [ImpactViolationType.COLD]: { r: 143, g: 182, b: 200 }, // cool tint of info
  [ImpactViolationType.LIGHT]: { r: 250, g: 218, b: 149 }, // gm-accent
  // Deep bronze was too dark to read as either a line on the chart surface or a
  // label on a panel — a violation type nobody can see is a violation type
  // nobody balances. Muted violet keeps the reading and clears the background.
  [ImpactViolationType.DARKNESS]: { r: 158, g: 128, b: 190 },
  [ImpactViolationType.NONE]: { r: 154, g: 144, b: 120 } // gm-text-muted
}
